import { startCase } from "lodash";

import { ImagePath } from "~/bff/types/ImagePath";
import { Store } from "~/bff/types/Store";
import { AVAILABLE_COUNTRIES } from "~/components/country-selector/constants";
import { paymentMethodCodeToNameMap } from "~/constants/paymentMethods";
import { CountryCode } from "~/helpers/country/types";
import { Nullable } from "~/types/general.types";
import { Config } from "~/utils/jsonLd";
import { JsonLdType } from "~/utils/jsonLd/constants";

const SOCIAL_LINKS = [
  "https://www.facebook.com/Primark/",
  "https://twitter.com/primark",
  "https://www.instagram.com/Primark/",
  "https://www.youtube.com/channel/UCIc8R2lxvKLtluGjN7e7TMw",
  "https://www.pinterest.co.uk/primark/",
];

const DEFAULT_PAYMENT_METHODS = [
  "Google Pay",
  "Apple Pay",
  "Credit card",
  "Cash",
  "Debit card",
];

const getPaymentMethodsNames = (paymentMethodsCodes: string[]): string[] => {
  return paymentMethodsCodes.map((code) => paymentMethodCodeToNameMap[code]);
};

export const getJsonLdConfig = (
  storeDetails: Nullable<Store> | undefined,
  pageUrl: string,
  logoImage: string,
): Config => {
  const openingHoursSpecification =
    storeDetails?.hours &&
    Object.entries(storeDetails.hours)
      .filter(([, value]) => typeof value === "object")
      .reduce<
        {
          "@type": "OpeningHoursSpecification";
          dayOfWeek: [string];
          opens: string;
          closes: string;
        }[]
      >((acc, [day, hour]) => {
        const lastDay = acc[acc?.length - 1];
        const currentDay = hour?.openIntervals?.[0];
        if (!currentDay) {
          return acc;
        }

        if (
          lastDay?.opens === currentDay?.start &&
          lastDay?.closes === currentDay?.end
        ) {
          lastDay?.dayOfWeek.push(startCase(day));
          return acc;
        }

        acc.push({
          "@type": JsonLdType.OPENING_HOURS_SPECIFICATION,
          dayOfWeek: [startCase(day)],
          opens: currentDay.start,
          closes: currentDay.end,
        });
        return acc;
      }, []);

  const departmentsData = storeDetails?.departments?.map((department) => {
    return {
      "@type": "Department",
      name: department?.departmentName,
      url: department?.departmentURL ?? undefined,
    };
  });

  const servicesData = storeDetails?.services?.map((service) => {
    return {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service?.name,
        url: service?.url ?? undefined,
      },
    };
  });

  const jsonLdConfig: Config = {
    placeholders: {
      "@id": storeDetails?.googlePlaceId,
      name: `${storeDetails?.name} ${storeDetails?.geomodifier}`,
      image: "googleProfilePhoto",
      telephone: storeDetails?.phoneNumber,
      url: pageUrl,
      "address.streetAddress": storeDetails?.address?.line1,
      "address.addressLocality": storeDetails?.address?.city,
      "address.addressRegion": storeDetails
        ? AVAILABLE_COUNTRIES[storeDetails?.countryCode as CountryCode]?.title
        : undefined,
      "address.postalCode": storeDetails?.address?.postalCode,
      "address.addressCountry": storeDetails
        ? AVAILABLE_COUNTRIES[storeDetails?.countryCode as CountryCode]?.title
        : undefined,
    },
    overrides: {
      openingHoursSpecification,
      paymentAccepted: storeDetails?.paymentOptions?.length
        ? getPaymentMethodsNames(storeDetails.paymentOptions as string[])
        : DEFAULT_PAYMENT_METHODS,
      sameAs: SOCIAL_LINKS,
      logo: logoImage,
      description: storeDetails?.description,
      department: departmentsData,
      makesOffer: servicesData,
      ...(storeDetails?.clickAndCollectStore && {
        availableDeliveryMethod: [{ "@type": "OnSitePickup" }],
      }),
    },
  };

  return jsonLdConfig;
};

export const processYextImage = (image: Nullable<ImagePath> | undefined) => {
  const { url, sourceUrl } = image ?? {};

  const isAmplienceImage = !!sourceUrl;
  const imageSrc = isAmplienceImage ? sourceUrl : url;
  return { isAmplienceImage, imageSrc };
};
